import Language from '@mui/icons-material/Language';
import ListItemButton from '@mui/material/ListItemButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { setLanguage } from '../../../assets/i18n/config';
import { LOG_MESSAGES } from '../../../constants/logMessages';
import { Languages, TranslatedLanguage } from '../../../interface';
import Box from '@mui/material/Box';
import { Icon } from '@/components/muiComponents/basic/icon/icon';
import { ThemedTooltip } from '@/components/chat/FeedbackContainer';

export default function LanguageSelector({ closed }: { closed: boolean }) {
  const { t } = useTranslation(['navMenu']);
  const browserLanguage = (navigator.language || 'en').slice(
    0,
    2,
  ) as TranslatedLanguage;

  const [selectedLanguage, setSelectedLanguage] =
    React.useState<TranslatedLanguage>(
      (localStorage.getItem('i18nextLng')?.slice(0, 2) as TranslatedLanguage) ||
        browserLanguage ||
        TranslatedLanguage.en,
    );

  const availableLanguages: {
    code: TranslatedLanguage;
    label: string;
    logMessage: string;
  }[] = [
    {
      code: TranslatedLanguage.en,
      label: t('menu.english'),
      logMessage: LOG_MESSAGES.USER_SELECTED_ENGLISH,
    },
    {
      code: TranslatedLanguage.fr,
      label: t('menu.french'),
      logMessage: LOG_MESSAGES.USER_SELECTED_FRENCH,
    },
    {
      code: TranslatedLanguage.es,
      label: t('menu.spanish'),
      logMessage: LOG_MESSAGES.USER_SELECTED_SPANISH,
    },
  ];

  const handleChange = (event: any) => {
    const selected = event?.target?.value as TranslatedLanguage;
    setSelectedLanguage(selected);

    switch (selected) {
      case TranslatedLanguage.en:
        setLanguage(Languages.ENGLISH);
        break;
      case TranslatedLanguage.fr:
        setLanguage(Languages.FRENCH);
        break;
      case TranslatedLanguage.es:
        setLanguage(Languages.SPANISH);
        break;
    }
    // Dispatch custom event to notify other components of language change
    window.dispatchEvent(new CustomEvent('languageChanged'));
  };

  React.useEffect(() => {
    handleChange({ target: { value: selectedLanguage } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ClosedIcon = (
    <ThemedTooltip
      title={t('language')}
      placement="right"
      data-testid="section-tooltip"
    >
      <Box
        data-testid="sidebar-dark-mode-toggle"
        width="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <ListItemButton
          sx={{
            padding: '10px 10px',
            borderRadius: '10px',
          }}
        >
          <Box display="flex" justifyContent="center" alignItems="center">
            <Icon icon={Language} sx={{ p: '2px' }} size="medium" />
          </Box>
        </ListItemButton>
      </Box>
    </ThemedTooltip>
  );

  if (closed) return ClosedIcon;

  return (
    <Box
      data-testid="language-selector"
      width="100%"
      display={closed ? 'none' : 'flex'}
      justifyContent="start"
      alignItems="center"
      sx={{ height: '44px' }}
      pl={1.5}
    >
      <Select
        value={selectedLanguage}
        onChange={handleChange}
        data-testid="language-dropdown"
        variant="outlined"
        size="small"
        sx={{
          // '& .MuiOutlinedInput-input': {
          //   padding: '12px 31px 12px 10px',
          // },
          border: '1px',
          borderRadius: '10px',
        }}
        renderValue={(selected) => {
          const currentLang = availableLanguages.find(
            (lang) => lang.code === selected,
          );
          return (
            <Box display="flex" alignItems="center">
              <Icon icon={Language} sx={{ mr: 1 }} size="medium" />
              {currentLang ? currentLang.label : ''}
            </Box>
          );
        }}
      >
        {availableLanguages.map((lang) => (
          <MenuItem key={lang.code} value={lang.code}>
            {lang.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}
